import { useTranslation } from "react-i18next";
import { formatDate } from "../../utils";
import { OfferResponse } from "../../types";
import FileReader from "../FileReader/FileReader";
import OfferPriceEditor from "./OfferPriceEditor";
import "./offerDisplay.scss";

/**
 * The buyer's read view of a seller's offers across a joint order (Task 7) — one card per line
 * item, each reusing the same `.offer-detail-row` layout `IsBuyerOffer.tsx` uses and the shared
 * `OfferPriceEditor` (the pre-Carbon file had its own copy of that editor under the same name;
 * de-duplicated into `./OfferPriceEditor.tsx`). Rendered by `components/Offer/Offer.tsx`
 * (unchanged `{ offers: OfferResponse[] }` prop) for JOINT/INVERSE_JOINT orders.
 */
const IsBuyerMultipleOffer = ({ offers }: { offers: OfferResponse[] }) => {
  const { t } = useTranslation();

  return (
    <div className="offer-display">
      <p className="offer-display__label">{t("offer.from")}</p>
      <h1 className="offer-display__company">
        {offers[0]?.user?.company?.name}{" "}
        <span className="offer-display__tax-id">
          {offers[0]?.user?.company?.taxId}
        </span>
      </h1>
      {offers[0]?.fileURL && (
        <div className="offer-display-multi__file">
          <FileReader fileKey={offers[0]?.fileURL} />
        </div>
      )}
      {offers?.map((offer) => (
        <div
          key={offer.id}
          className={`offer-display-multi__card${
            offer.selected ? " offer-display-multi__card--selected" : ""
          }`}
        >
          <h1 className="offer-display-multi__material-name">
            {offer?.order?.material?.globalMaterialName}
          </h1>

          <div className="offer-detail-row">
            <p className="offer-detail-row__label">{t("offer.table.price")}</p>
            <div className="offer-detail-row__value">
              <OfferPriceEditor offer={offer} />
            </div>
          </div>

          <div className="offer-detail-row">
            <p className="offer-detail-row__label">
              {t("offer.table.quantity")}
            </p>
            <p className="offer-detail-row__value">
              {offer?.order?.quantity} {offer?.order?.measurementUnit}
            </p>
          </div>

          {offer?.selected && (
            <div className="offer-detail-row">
              <p className="offer-detail-row__label">
                {t("offer.table.quantitySelected")}
              </p>
              <p className="offer-detail-row__value">
                {offer?.price} {offer?.currency} /{" "}
                {offer?.order?.measurementUnit}
                <br /> {offer?.quantity} {offer?.order?.measurementUnit}
              </p>
            </div>
          )}

          {offer?.expireDate && (
            <div className="offer-detail-row">
              <p className="offer-detail-row__label">
                {t("offer.table.expireDate")}
              </p>
              <p className="offer-detail-row__value">
                {formatDate(offer?.expireDate)}
              </p>
            </div>
          )}

          {offer?.comment && (
            <div className="offer-detail-row">
              <p className="offer-detail-row__label">{t("offer.comments")}</p>
              <p className="offer-detail-row__value">{offer?.comment}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default IsBuyerMultipleOffer;
